import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { MainLayout } from '../layouts/MainLayout';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { useAuth } from '../contexts/AuthContext';
import {
  useConnectEmail,
  useCreateCheckout,
  useCreatePortalSession,
  useCreateTelegramLinkToken,
  useDisconnectEmail,
  useDisconnectTelegram,
  useNotificationChannels,
  useUserPlan,
} from '../hooks/useApi';
import {
  AlertCircle,
  ArrowUpRight,
  Bot,
  Check,
  CheckCircle2,
  Copy,
  CreditCard,
  ExternalLink,
  Lock,
  Loader2,
  Mail,
  MessageCircle,
  Plug,
  RefreshCw,
  Sparkles,
  Unlink,
} from 'lucide-react';
import type { TelegramLinkTokenResponse } from '../types';

const MCP_URL = `${(import.meta.env.VITE_API_PROXY_TARGET || 'https://api.hellacamping.com').replace(/\/$/, '')}/mcp`;

export function SettingsPage() {
  const { isLoading: isAuthLoading } = useRequireAuth();
  const { user } = useAuth();
  const { data: plan, isLoading: isPlanLoading, refetch: refetchPlan } = useUserPlan();
  const checkout = useCreateCheckout();
  const portal = useCreatePortalSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const telegramLocked = plan ? !plan.allowedChannels.includes('telegram') : false;
  const [latestLink, setLatestLink] = useState<TelegramLinkTokenResponse | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [urlCopyState, setUrlCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [isLinkPolling, setIsLinkPolling] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'advanced'>('general');

  // Detect post-checkout return
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const sessionId = typeof search.session_id === 'string' ? search.session_id : null;

  useEffect(() => {
    if (sessionId) {
      navigate({ to: '/settings', search: {}, replace: true });
      queryClient.invalidateQueries({ queryKey: ['billing', 'plan'] });
      refetchPlan();
    }
  }, [sessionId, navigate, queryClient, refetchPlan]);

  const shouldPollForLink = isLinkPolling && !!latestLink;

  const {
    data: channels,
    isLoading: isChannelsLoading,
    error: channelsError,
    refetch,
  } = useNotificationChannels({
    refetchInterval: shouldPollForLink ? 2000 : false,
  });
  const createLink = useCreateTelegramLinkToken();
  const disconnect = useDisconnectTelegram();
  const connectEmail = useConnectEmail();
  const disconnectEmail = useDisconnectEmail();

  const activeTelegramChannel = useMemo(
    () =>
      channels?.find(
        (channel) => channel.channel === 'telegram' && channel.status === 'active'
      ) ?? null,
    [channels]
  );

  const activeEmailChannel = useMemo(
    () =>
      channels?.find(
        (channel) => channel.channel === 'email' && channel.status === 'active'
      ) ?? null,
    [channels]
  );

  useEffect(() => {
    if (activeTelegramChannel && isLinkPolling) {
      setIsLinkPolling(false);
    }
  }, [activeTelegramChannel, isLinkPolling]);

  const isLoading = isAuthLoading || isChannelsLoading;
  const isPro = plan?.tier === 'pro';
  const isCanceling = plan?.status === 'canceling' || plan?.status === 'canceled_grace';

  const onGenerateLink = async () => {
    setCopyState('idle');
    try {
      const response = await createLink.mutateAsync();
      setLatestLink(response);
      setIsLinkPolling(true);
      await refetch();
    } catch {
      setIsLinkPolling(false);
      // Error displayed by mutation state.
    }
  };

  const onCopyStartCommand = async () => {
    if (!latestLink) {
      return;
    }

    try {
      await navigator.clipboard.writeText(`/start ${latestLink.token}`);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };

  const onCopyMcpUrl = async () => {
    try {
      await navigator.clipboard.writeText(MCP_URL);
      setUrlCopyState('copied');
    } catch {
      setUrlCopyState('failed');
    }
    setTimeout(() => setUrlCopyState('idle'), 2000);
  };

  const onDisconnect = async () => {
    try {
      await disconnect.mutateAsync();
      setLatestLink(null);
      setCopyState('idle');
      setIsLinkPolling(false);
      await refetch();
    } catch {
      // Error displayed by mutation state.
    }
  };

  const onConnectEmail = async () => {
    if (!user?.email) return;
    try {
      await connectEmail.mutateAsync(user.email);
      await refetch();
    } catch {
      // Error displayed by mutation state.
    }
  };

  const onDisconnectEmail = async () => {
    try {
      await disconnectEmail.mutateAsync();
      await refetch();
    } catch {
      // Error displayed by mutation state.
    }
  };

  return (
    <MainLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-1">Manage your notification channels</p>
        </div>

        <div role="tablist" aria-label="Settings sections" className="flex items-center gap-6 border-b border-gray-200">
          <button
            type="button"
            role="tab"
            id="settings-tab-general"
            aria-selected={activeTab === 'general'}
            aria-controls="settings-panel-general"
            onClick={() => setActiveTab('general')}
            className={`-mb-px px-1 pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'general'
                ? 'border-emerald-600 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            General
          </button>
          <button
            type="button"
            role="tab"
            id="settings-tab-advanced"
            aria-selected={activeTab === 'advanced'}
            aria-controls="settings-panel-advanced"
            onClick={() => setActiveTab('advanced')}
            className={`-mb-px px-1 pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'advanced'
                ? 'border-emerald-600 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Advanced
          </button>
        </div>

        {activeTab === 'general' && (
          <div
            role="tabpanel"
            id="settings-panel-general"
            aria-labelledby="settings-tab-general"
            className="space-y-6"
          >
        <div className={`bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-6 ${telegramLocked ? 'relative' : ''}`}>
          {telegramLocked && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] rounded-xl z-10 flex items-center justify-center">
              <div className="text-center p-6">
                <Lock className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Telegram notifications require a Pro plan.
                </p>
                <a
                  href="#pro"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
                >
                  Upgrade to Pro
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Telegram Notifications</h2>
              <p className="text-sm text-gray-600 mt-1">
                Connect your Telegram DM to receive availability alerts.
              </p>
            </div>
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>

          {isLoading && (
            <div className="flex items-center gap-2 text-gray-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading channel status...
            </div>
          )}

          {!isLoading && channelsError && (
            <div className="p-4 rounded-lg border border-red-200 bg-red-50 text-red-700 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 mt-0.5" />
              <span>{channelsError.message}</span>
            </div>
          )}

          {!isLoading && !channelsError && activeTelegramChannel && (
            <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 text-emerald-800 font-medium">
                    <CheckCircle2 className="w-5 h-5" />
                    Telegram connected
                  </div>
                  <p className="text-sm text-emerald-700 mt-1">
                    Chat ID: {activeTelegramChannel.telegramChatId ?? 'Unknown'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onDisconnect}
                  disabled={disconnect.isPending}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  {disconnect.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Unlink className="w-4 h-4" />
                  )}
                  Disconnect
                </button>
              </div>
            </div>
          )}

          {!isLoading && !channelsError && !activeTelegramChannel && (
            <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-800">
              Telegram is not connected yet.
            </div>
          )}

          <div className="space-y-3">
            <button
              type="button"
              onClick={onGenerateLink}
              disabled={createLink.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {createLink.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <MessageCircle className="w-4 h-4" />
              )}
              Generate Telegram Link
            </button>

            {shouldPollForLink && !activeTelegramChannel && (
              <div className="p-3 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-sm flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Waiting for Telegram confirmation...
              </div>
            )}

            {createLink.error && (
              <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm">
                {createLink.error.message}
              </div>
            )}

            {latestLink && (
              <div className="p-4 rounded-lg border border-gray-200 bg-gray-50 space-y-3">
                <p className="text-sm text-gray-700">{latestLink.instructions}</p>

                {latestLink.startLink ? (
                  <a
                    href={latestLink.startLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-emerald-700 hover:text-emerald-800 font-medium"
                  >
                    Open Telegram start link
                    <ExternalLink className="w-4 h-4" />
                  </a>
                ) : (
                  <p className="text-sm text-gray-600">
                    Bot username is not configured. Copy and send the start command manually.
                  </p>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                  <code className="px-3 py-2 rounded bg-white border border-gray-200 text-sm">
                    /start {latestLink.token}
                  </code>
                  <button
                    type="button"
                    onClick={onCopyStartCommand}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-white"
                  >
                    <Copy className="w-4 h-4" />
                    {copyState === 'copied'
                      ? 'Copied'
                      : copyState === 'failed'
                        ? 'Copy failed'
                        : 'Copy'}
                  </button>
                </div>

                <p className="text-xs text-gray-500">
                  Link expires at {new Date(latestLink.expiresAt).toLocaleString()}.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Email Notifications</h2>
              <p className="text-sm text-gray-600 mt-1">
                Receive availability alerts via email using your login address.
              </p>
            </div>
          </div>

          {isLoading && (
            <div className="flex items-center gap-2 text-gray-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading channel status...
            </div>
          )}

          {!isLoading && !channelsError && activeEmailChannel && (
            <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 text-emerald-800 font-medium">
                    <CheckCircle2 className="w-5 h-5" />
                    Email connected
                  </div>
                  <p className="text-sm text-emerald-700 mt-1">
                    {activeEmailChannel.email}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onDisconnectEmail}
                  disabled={disconnectEmail.isPending}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  {disconnectEmail.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Unlink className="w-4 h-4" />
                  )}
                  Disconnect
                </button>
              </div>
            </div>
          )}

          {!isLoading && !channelsError && !activeEmailChannel && (
            <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-800">
              Email notifications are not enabled yet.
            </div>
          )}

          {!isLoading && !channelsError && !activeEmailChannel && (
            <div className="space-y-3">
              <button
                type="button"
                onClick={onConnectEmail}
                disabled={connectEmail.isPending || !user?.email}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {connectEmail.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4" />
                )}
                Enable Email Notifications
              </button>
              {user?.email && (
                <p className="text-sm text-gray-500">
                  Notifications will be sent to <span className="font-medium text-gray-700">{user.email}</span>
                </p>
              )}
              {connectEmail.error && (
                <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm">
                  {connectEmail.error.message}
                </div>
              )}
            </div>
          )}

          {disconnectEmail.error && (
            <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm">
              {disconnectEmail.error.message}
            </div>
          )}
        </div>

        {/* Pro Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-gray-900">Pro</h2>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    isPro
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {isPro ? 'Pro' : 'Free'}
                </span>
              </div>
              {isPro && plan?.currentPeriodEnd && (
                <p className="text-sm text-gray-600 mt-1">
                  {isCanceling
                    ? `Cancels ${new Date(plan.currentPeriodEnd).toLocaleDateString()}`
                    : `Renews ${new Date(plan.currentPeriodEnd).toLocaleDateString()}`}
                </p>
              )}
            </div>
            {isPro && (
              <button
                type="button"
                onClick={() => portal.mutate()}
                disabled={portal.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {portal.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4" />
                )}
                Manage Subscription
              </button>
            )}
          </div>

          {portal.error && (
            <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              {portal.error.message}
            </div>
          )}

          {isPlanLoading && (
            <div className="flex items-center gap-2 text-gray-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading plan...
            </div>
          )}

          {!isPlanLoading && (
            <>
              {/* Feature Comparison */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Plan Features</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="font-medium text-gray-500">Feature</div>
                  <div className="font-medium text-gray-500 text-center">Free</div>
                  <div className="font-medium text-emerald-700 text-center">Pro</div>

                  <div className="text-gray-700">Active alerts</div>
                  <div className="text-center text-gray-600">1</div>
                  <div className="text-center text-emerald-700 font-medium">Unlimited</div>

                  <div className="text-gray-700">Notification channels</div>
                  <div className="text-center text-gray-600">Email</div>
                  <div className="text-center text-emerald-700 font-medium">Email + Telegram</div>

                  <div className="text-gray-700">Notification cooldown</div>
                  <div className="text-center text-gray-600">30 min</div>
                  <div className="text-center text-emerald-700 font-medium">10 min</div>
                </div>
              </div>

              {/* Pricing Cards (for free users) */}
              {!isPro && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="rounded-lg border border-gray-200 p-4">
                    <div className="mb-3">
                      <h3 className="font-semibold text-gray-900">Monthly</h3>
                      <div className="mt-1">
                        <span className="text-2xl font-bold text-gray-900">$6</span>
                        <span className="text-gray-500 ml-1">/month</span>
                      </div>
                    </div>
                    <ul className="space-y-1.5 mb-4 text-sm text-gray-600">
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                        Unlimited active alerts
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                        Email + Telegram notifications
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                        10-minute notification cooldown
                      </li>
                    </ul>
                    <button
                      type="button"
                      onClick={() => checkout.mutate({ interval: 'monthly', email: user?.email })}
                      disabled={checkout.isPending}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {checkout.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CreditCard className="w-4 h-4" />
                      )}
                      Subscribe
                    </button>
                  </div>

                  <div className="rounded-lg border-2 border-emerald-500 p-4 relative">
                    <div className="absolute -top-3 left-4">
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-600 text-white">
                        <Sparkles className="w-3 h-3" />
                        Save 44%
                      </span>
                    </div>
                    <div className="mb-3">
                      <h3 className="font-semibold text-gray-900">Annual</h3>
                      <div className="mt-1">
                        <span className="text-2xl font-bold text-gray-900">$40</span>
                        <span className="text-gray-500 ml-1">/year</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">~$3.33/month</p>
                    </div>
                    <ul className="space-y-1.5 mb-4 text-sm text-gray-600">
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                        Unlimited active alerts
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                        Email + Telegram notifications
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                        10-minute notification cooldown
                      </li>
                    </ul>
                    <button
                      type="button"
                      onClick={() => checkout.mutate({ interval: 'annual', email: user?.email })}
                      disabled={checkout.isPending}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {checkout.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CreditCard className="w-4 h-4" />
                      )}
                      Subscribe
                    </button>
                  </div>
                </div>
              )}

              {checkout.error && (
                <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  {checkout.error.message}
                </div>
              )}
            </>
          )}
        </div>
          </div>
        )}

        {activeTab === 'advanced' && (
          <div
            role="tabpanel"
            id="settings-panel-advanced"
            aria-labelledby="settings-tab-advanced"
            className="space-y-6"
          >
        {/* MCP / AI Assistants Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-6">
          <div>
            <div className="flex items-center gap-2">
              <Plug className="w-5 h-5 text-gray-700" />
              <h2 className="text-lg font-semibold text-gray-900">Connect AI assistants (MCP)</h2>
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                Beta
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Use Hellacamping from Claude, Codex, and other MCP-compatible AI assistants — they
              act on your behalf using your account.
            </p>
          </div>

          <div className="flex items-start gap-2 text-sm text-gray-700">
            <Bot className="w-4 h-4 mt-0.5 shrink-0 text-gray-500" />
            <p>
              Once connected, the assistant can search parks, check campsite availability, and
              manage your alerts on your behalf via the Model Context Protocol.
            </p>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Server URL</p>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="px-3 py-2 rounded bg-gray-50 border border-gray-200 text-sm break-all">
                {MCP_URL}
              </code>
              <button
                type="button"
                onClick={onCopyMcpUrl}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                {urlCopyState === 'copied' ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
                {urlCopyState === 'copied'
                  ? 'Copied'
                  : urlCopyState === 'failed'
                    ? 'Copy failed'
                    : 'Copy'}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Setup</p>
            <ol className="list-decimal pl-5 space-y-1.5 text-sm text-gray-600">
              <li>
                In your assistant's connector settings (e.g. <strong>Claude</strong> → Settings →
                Connectors → add a custom/remote MCP server, or <strong>Codex</strong>/another MCP
                client's equivalent), paste the server URL above.
              </li>
              <li>
                When prompted, sign in with your Hellacamping account (the same login you use here)
                and approve access.
              </li>
              <li>The assistant can now use your Hellacamping tools.</li>
            </ol>
          </div>
        </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
